import { IconButton } from "@mui/material";
import {
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortItem,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import PageLayout from "@/components/PageLayout";
import RenderWithTooltip, {
  CustomTrimmedTooltip,
} from "@/components/RenderWithTooltip";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import ActionMenu from "@/components/app/ActionMenu";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import DeleteModal from "@/components/app/DeleteModal";
import { useRoleController } from "@/components/useRoleController";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useDebounce } from "@/hooks/use-debounce";
import { useQueryParams } from "@/hooks/use-query-param";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { useListTasks } from "@/queries/collaboration/tasks/use-list-task";
import { useArchiveTask } from "@/queries/collaboration/tasks/use-task-archive";
import { useTaskCancel } from "@/queries/collaboration/tasks/use-task-cancel";
import {
  GetAllTasksTaskPriorityEnum,
  GetAllTasksTaskStatusEnum,
} from "@/services";
import { PRIMITIVE } from "@/tokens/design-tokens";
import {
  formatDate,
  formatDateTime,
  formatTime,
  getStartEndDateOfSameDay,
} from "@/utils/date-time-utility";
import { PatientName } from "@/utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  // hasActiveFilters,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "@/utils/utils";
import SendBulkEmailModal from "@/pages/app/provider/communications/email/SendBulkEmailModal";
import SMSModal from "@/pages/app/provider/dashboard/sections/dashboard-questionnaire-review/SMSModal";
import { ContactDialModal } from "@/pages/app/provider/dashboard/twilio/ContactDial";
import { PRIORITY_MAPPING } from "../collaboration-referral-constant";
import {
  ACTIVITY_MAPPING,
  DEFAULT_FILTERS,
  FLAG_MAPPING,
  INITIAL_EXPANDED,
  TaskFilters,
  dynamicColumns,
} from "../collaboration-task-constant";
import BatchReassignTaskDrawer from "./BatchReassignTaskDrawer";
import BatchResolveDrawer from "./BatchResolveDrawer";
import ReviewNoteModal from "./ReviewNoteModal";
import ReviewTaskDrawer from "./ReviewTaskDrawer";
import TaskTopBar from "./TaskTopBar";
import UpsertTaskDrawer from "./UpsertTaskDrawer";
import { TaskCalendar } from "./task-calendar/TaskCalendar";
import { getStoredRealm } from "@/utils/auth-storage";

const TASK_STATUS_COLOR: Record<string, { color: string; bgColor: string }> = {
  TO_DO: {
    color: PRIMITIVE.color.orange[700],
    bgColor: PRIMITIVE.color.orange[100],
  },
  RESOLVED: {
    color: PRIMITIVE.color.brandBlue[700],
    bgColor: PRIMITIVE.color.brandBlue[25],
  },
};

interface TasksProps {
  tableHeight?: string;
  patientData?: any;
}

export type ActiveTab = "patient-centric-tasks" | "provider-centric-tasks";

const MINIMIZED_VIEW_FIELDS = [
  "taskTitle",
  "due_date",
  "activity",
  "taskStatus",
];

const Tasks = ({ patientData }: TasksProps) => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [pagination, setPagination] =
    useState<GridPaginationModel>(DEF_PAGINATION);
  const { hasUpdate, hasDelete, hasReadOnly } = useRoleController("Task");

  const [taskFilter, setTaskFilter] = useState<TaskFilters>(DEFAULT_FILTERS);
  const [searchStringKey, setSearchStringKey, instantSearchVal] =
    useDebounce<string>("");
  const [expanded, setExpanded] =
    useState<Record<string, boolean>>(INITIAL_EXPANDED);
  const myTasksParam = useQueryParams("myTasks");
  const [todoTaskFilter, setTodoTaskFilter] = useState({
    myTasks: myTasksParam === "false" ? false : true,
    toDoTask: true,
  });

  const { patientId } = useParams();
  const [selectedTaskModel, setSelectedTaskModel] =
    useState<GridRowSelectionModel>([]);
  const isBulkSelectionEnabled = !isVisitNotePage && todoTaskFilter.toDoTask;

  // Community DataGrid throws if it receives a multi-row model while
  // checkboxes are off, so drop the selection as soon as bulk mode turns off.
  useEffect(() => {
    if (!isBulkSelectionEnabled) setSelectedTaskModel([]);
  }, [isBulkSelectionEnabled]);

  const activeTask = useQueryParams("activeTask") || "patient-centric-tasks";
  const [
    taskDisclosureHost,
    resolveTaskDisclosureHost,
    reassignTaskDisclosureHost,
    deleteTaskDisclosureHost,
    cancelTaskDisclosureHost,
    sendMailDisclosure,
    confirmResolveDisc,
    reviewTaskDisclosureHost,
    reviewNoteDisclosureHost,
    contactDialModalDisclosure,
    smsModalDisclosure,
  ] = useReduceDisclosure(11);
  const { mutateAsync: archiveTask } = useArchiveTask();
  const { mutateAsync: cancelTask } = useTaskCancel();
  const isProviderTask =
    useQueryParams("activeTask") === "provider-centric-tasks";

  // ECRH-4232: Safely parse the due date (CustomDatePicker emits a Dayjs).
  // Coerce to end-of-day so the BE treats it as an inclusive upper bound.
  const dueDate = (() => {
    if (!taskFilter.due_date) return undefined;
    const d = dayjs(taskFilter.due_date);
    if (!d.isValid()) return undefined;
    return d.endOf("day").toDate();
  })();

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [taskType, setTaskType] = useState<string>("");
  const [viewType, setViewType] = useState<"list" | "calender">("list");
  const { endTime, startTime } = getStartEndDateOfSameDay(dayjs(new Date()));
  const [filterDate, setFilterDate] = useState<[string, string]>([
    startTime ?? new Date()?.toISOString(),
    endTime ?? new Date()?.toISOString(),
  ]);

  const {
    data: tasks,
    isFetching,
    refetch,
  } = useListTasks({
    pagination: pagination,
    gridSorting: gridSorting,
    taskStatus: taskFilter.status as GetAllTasksTaskStatusEnum,
    taskPriority: taskFilter.priority as GetAllTasksTaskPriorityEnum,
    actionType: taskFilter.action_type ?? undefined,
    providerTask: isProviderTask,
    dueDate: dueDate ? dueDate.toISOString() : undefined,
    patient: patientId ?? undefined,
    assignTo: getIdFromObj(taskFilter.assign_to),
    assignBy: getIdFromObj(taskFilter.assign_by),
    archive: false,
    xTENANTID: getXtenantId(),
    myTask: todoTaskFilter.myTasks,
    toDoTask: todoTaskFilter.toDoTask,
    searchString: searchStringKey?.length ? searchStringKey : undefined,
    startDate: viewType === "calender" ? filterDate?.[0] : undefined,
    endDate: viewType === "calender" ? filterDate?.[1] : undefined,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    (searchParams.get("activeTask") as ActiveTab) ?? "patient-centric-tasks"
  );

  const COLUMNS = applyDefaultColumnSettings(
    [
      {
        field: "taskTitle",
        headerName: "Title",
        flex: 0.12,
        minWidth: 100,
        renderCell: ({ row }) => <CustomTrimmedTooltip value={row?.title} />,
      },
      {
        field: "name",
        headerName: "Patient Name",
        flex: 0.13,
        minWidth: 110,
        renderCell: ({ row }) =>
          row?.patient ? (
            <PatientName
              patientId={getIdFromObj(row?.patient)!}
              name={getValueFromObj(row?.patient) as any}
            />
          ) : (
            <Text isTabletext>-</Text>
          ),
      },
      {
        field: "due_date",
        headerName: "Due Date & Time",
        flex: 0.14,
        minWidth: 140,
        renderCell: ({ row }: { row: any }) => (
          <Flex gap={0.3}>
            <Text isTabletext>{formatDate(row.dueDate)}</Text>,
            <Text isTabletext>{formatTime(row.dueDate)}</Text>
          </Flex>
        ),
      },
      {
        field: "taskStatus",
        headerName: "Status",
        flex: 0.08,
        minWidth: 80,
        renderCell: ({ row }) => {
          const status = row?.taskStatus;
          const label = status
            ? replaceUnderScoreAddSpace(status?.toLowerCase())
            : "-";
          const colors = TASK_STATUS_COLOR[status] ?? TASK_STATUS_COLOR.TO_DO;
          return (
            <StatusChip
              label={label}
              color={colors.color}
              bgColor={colors.bgColor}
            />
          );
        },
      },
      {
        field: "task_priority",
        headerName: "Priority",
        flex: 0.08,
        minWidth: 80,
        renderCell: ({ row }: { row: any }) => {
          const priorityConfig =
            PRIORITY_MAPPING[row?.priority as keyof typeof PRIORITY_MAPPING];
          if (!priorityConfig) return "-";

          const variantMap: Record<string, "error" | "warning" | "default"> = {
            HIGH: "error",
            MEDIUM: "warning",
            LOW: "default",
          };

          return (
            <StatusChip
              label={priorityConfig.label}
              variant={variantMap[row?.priority] ?? "default"}
            />
          );
        },
      },
      {
        field: "activity",
        headerName: "Activity",
        flex: 0.06,
        minWidth: 60,
        renderCell: ({ row }: { row: any }) => {
          // ECRH-4513: Show "-" when task has no linked patient
          if (!row?.patient) return <Text isTabletext>-</Text>;

          const action = row?.actions
            ? Object.values(row?.actions)[0]
            : "Other";

          const activityConfig =
            ACTIVITY_MAPPING[action as keyof typeof ACTIVITY_MAPPING] ||
            ACTIVITY_MAPPING.Visit;

          if (!activityConfig.icon) return null;

          const Icon = activityConfig.icon;
          // ECRH-4232: per Bhushan, Visit & Other have no action — render the
          // icon as non-clickable. Email/Call/Message open their respective modals.
          const isClickable =
            action === "Email" || action === "Call" || action === "Message";

          if (!isClickable) {
            return (
              <IconButton disableRipple sx={{ cursor: "default" }}>
                <Icon fontSize="small" sx={{ color: activityConfig.color }} />
              </IconButton>
            );
          }

          return (
            <IconButton
              onClick={() => {
                const actionDetail = getActionValue(row);
                setSelectedTaskForActivity(row);
                if (action === "Email") {
                  sendMailDisclosure.open([actionDetail]);
                } else if (action === "Call") {
                  contactDialModalDisclosure.open(actionDetail.uuid);
                } else if (action === "Message") {
                  smsModalDisclosure.open(actionDetail);
                }
              }}
            >
              <Icon fontSize="small" sx={{ color: activityConfig.color }} />
            </IconButton>
          );
        },
      },
      {
        field: "assignBy",
        headerName: "Assigned By",
        flex: 0.1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <RenderWithTooltip value={getValueFromObj(row?.assignBy) as any} />
        ),
      },
      {
        field: "assignTo",
        headerName: isProviderTask ? "Assigned To" : "Task Reviewer",
        flex: 0.1,
        minWidth: 100,
        renderCell: ({ value }: any) => (
          <RenderWithTooltip value={getValueFromObj(value) as any} />
          // <Text>{getValueFromObj(value) as any}</Text>
        ),
      },
      {
        field: "resolvedDate",
        headerName: "Completed Date & Time",
        flex: 0.12,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Flex gap={0.6} flexDirection={"column"}>
            <RenderWithTooltip
              value={row.resolvedDate ? formatDateTime(row.resolvedDate) : "-"}
            />
          </Flex>
        ),
      },
      {
        field: "resolvedBy",
        headerName: "Resolved By",
        flex: 0.1,
        minWidth: 100,
        renderCell: ({ row }) => <RenderWithTooltip value={row?.resolvedBy} />,
      },

      {
        field: "reviewedStatus",
        headerName: "Review Status",
        flex: 0.11,
        renderCell: ({ value }: any) => {
          return value ? value : "-";
        },
      },
      {
        field: "reviewedDate",
        headerName: "Reviewed On",
        flex: 0.14,
        renderCell: ({ row }: any) => (
          <Flex gap={0.6} flexDirection={"column"}>
            <RenderWithTooltip
              value={row?.reviewedDate ? formatDateTime(row.reviewedDate) : "-"}
            />
          </Flex>
        ),
      },
      {
        field: "description",
        headerName: "Note",
        flex: 0.14,
        minWidth: 120,
        renderCell: ({ row }: { row: any }) => (
          <RenderWithTooltip value={row?.description} />
        ),
      },
      {
        field: "flagStatus",
        headerName: "Flags",
        flex: 0.05,
        minWidth: 50,
        renderCell: ({ row }: { row: any }) => {
          const flagConfig =
            FLAG_MAPPING[row?.flagStatus as keyof typeof FLAG_MAPPING] ||
            FLAG_MAPPING.NONE;

          const Icon = flagConfig?.icon;
          return Icon ? (
            <Icon fontSize="small" sx={{ color: flagConfig.color }} />
          ) : (
            "-"
          );
        },
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        renderCell: ({ row }: any) => {
          const isReviewed = row?.reviewedStatus === "REVIEWED";
          // Provider-centric resolved tasks are terminal — no further actions allowed.
          if (row?.taskStatus === "RESOLVED" && isProviderTask) return null;

          // Mark as Reviewed: shown only on Patient-Centric tab when the patient
          // has resolved the task and a provider has not yet reviewed it.
          // Reviewed tasks cannot be reviewed again (spec: no duplicate reviews).
          const canReview =
            !isProviderTask && row?.taskStatus === "RESOLVED" && !isReviewed;
          // Patient-Centric-only actions: Mark as Reviewed (pending review) and
          // Review Note (read-only, after review). Provider-Centric tab gets none
          // of these — review workflow does not apply there.
          // ECRH-5030: revamped Patient-Centric grid only exposes Mark as
          // Reviewed / Edit / Cancel. "Review Note" is commented out to keep
          // the dead branch discoverable in case the workflow is restored.
          const patientCentricOptions = !isProviderTask
            ? [
                ...(canReview
                  ? [
                      {
                        value: "reviewed",
                        isDisabled: false,
                        hoverMsg: "",
                        label: "Mark as Reviewed",
                      },
                    ]
                  : []),
                // ...(isReviewed
                //   ? [
                //       {
                //         value: "reviewNote",
                //         isDisabled: false,
                //         hoverMsg: "",
                //         label: "Review Note",
                //       },
                //     ]
                //   : []),
              ]
            : [];

          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  {
                    value: "edit",
                    isDisabled: row?.taskStatus === "RESOLVED",
                    hoverMsg: "You cannot Perform Operation on Resolved Task",
                    label: "Edit",
                  },
                  ...patientCentricOptions,
                  // Resolve / Reassign: Provider-Centric tab only.
                  // Patient-Centric tasks are resolved by the patient via the
                  // Patient Portal, so providers must not resolve or reassign them.
                  ...(isProviderTask
                    ? [
                        {
                          value: "resolve",
                          isDisabled: disabledTask(row),
                          hoverMsg: getHoverMsg(),
                          label: "Resolve",
                        },
                        {
                          value: "reassign",
                          isDisabled: disabledTask(row),
                          hoverMsg: getHoverMsg(),
                          label: "Reassign",
                        },
                      ]
                    : []),
                  {
                    value: "cancel",
                    isDisabled: disabledTask(row),
                    hoverMsg: getHoverMsg(),
                    label: "Cancel Task",
                  },
                  // {
                  //   value: "delete",
                  //   isDisabled: disabledTask(row),
                  //   hoverMsg: getHoverMsg(),
                  //   label: "Delete",
                  // },
                ],
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                  resolve: hasUpdate,
                  reassign: hasUpdate,
                  cancel: hasUpdate,
                },
                removeOtherFields: hasReadOnly,
              })}
              onActionClick={(action) => {
                if (action === "edit")
                  taskDisclosureHost.open(getTaskDataEditPayload(row));
                if (action === "resolve")
                  resolveTaskDisclosureHost.open([row?.uuid]);
                if (action === "reassign")
                  reassignTaskDisclosureHost.open([row?.uuid]);
                if (action === "reviewed") reviewTaskDisclosureHost.open(row);
                if (action === "reviewNote") reviewNoteDisclosureHost.open(row);
                if (action === "cancel")
                  cancelTaskDisclosureHost.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["name", "due_date", "task_priority", "created", "taskTitle"]
  );

  async function archiveHandler<T>(value: boolean, id: T) {
    return archiveTask({
      taskId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  async function cancelTaskHandler<T>(id: T) {
    return cancelTask({
      bulkCancel: {
        taskId: [id as string],
      },
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  const [selectedTaskForActivity, setSelectedTaskForActivity] =
    useState<any>(null);

  const getActionValue = (row: any) => {
    const uuid = isProviderTask
      ? getIdFromObj(row?.assignTo)
      : getIdFromObj(row?.patient);
    const name = isProviderTask
      ? getValueFromObj(row?.assignTo)
      : getValueFromObj(row?.patient);
    return {
      uuid,
      name,
    };
  };

  const [me] = useCurrentUser();

  const getCurrentUser = (assignTo: any) => me?.uuid === getIdFromObj(assignTo);

  const disabledTask = (row: any) => !getCurrentUser(row?.assignTo);

  const getHoverMsg = () => "This task has not been assigned to you.";

  const handleConfirmResolve = () => {
    resolveTaskDisclosureHost.open([selectedTaskForActivity?.uuid]);
    confirmResolveDisc.close();
  };

  const allColumns =
    dynamicColumns(
      COLUMNS,
      patientData ?? false,
      isProviderTask,
      todoTaskFilter
    ) ?? [];

  const columns = isVisitNotePage
    ? allColumns.filter((col) => MINIMIZED_VIEW_FIELDS.includes(col.field))
    : allColumns;

  return (
    <PageLayout
      header={
        <TaskTopBar
          viewType={viewType}
          setViewType={setViewType}
          taskType={taskType}
          patientData={patientData}
          setTaskType={setTaskType}
          setSelectedTaskModel={setSelectedTaskModel}
          selectedTaskModel={selectedTaskModel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setSearchParams={setSearchParams}
          setTodoTaskFilter={setTodoTaskFilter}
          setExpanded={setExpanded}
          setFilters={setTaskFilter}
          filters={taskFilter}
          expanded={expanded}
          todoTaskFilter={todoTaskFilter}
          setSearchStringKey={setSearchStringKey}
          searchStringKey={instantSearchVal}
        />
      }
    >
      <ConfirmationModal
        maxWidth={"sm"}
        onConfirm={handleConfirmResolve}
        disclosureHost={confirmResolveDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"24px"}>
            Do you want resolve this task?
          </Text>
        }
      />

      {taskDisclosureHost.opened && (
        <UpsertTaskDrawer
          isProviderCentric={activeTab === "provider-centric-tasks"}
          disclosureHost={taskDisclosureHost}
          patientData={patientData}
          taskType={taskType}
        />
      )}

      <BatchResolveDrawer
        disclosureHost={resolveTaskDisclosureHost}
        setSelectedTaskModel={setSelectedTaskModel}
      />

      {reviewTaskDisclosureHost.opened && (
        <ReviewTaskDrawer disclosureHost={reviewTaskDisclosureHost} />
      )}

      {reviewNoteDisclosureHost.opened && (
        <ReviewNoteModal disclosureHost={reviewNoteDisclosureHost} />
      )}

      {reassignTaskDisclosureHost.opened && (
        <BatchReassignTaskDrawer
          title="Reassign Task"
          disclosureHost={reassignTaskDisclosureHost}
          setSelectedTaskModel={setSelectedTaskModel}
        />
      )}

      <DeleteModal
        w="380px"
        open={deleteTaskDisclosureHost.opened}
        onClose={deleteTaskDisclosureHost.close}
        id={deleteTaskDisclosureHost.data}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete Task"}
      />
      <DeleteModal
        w="380px"
        open={cancelTaskDisclosureHost.opened}
        onClose={cancelTaskDisclosureHost.close}
        id={cancelTaskDisclosureHost.data}
        onConfirmation={(id) => cancelTaskHandler(id)}
        content={"You want to cancel Task"}
      />

      {sendMailDisclosure.opened && (
        <SendBulkEmailModal
          disClosureHost={sendMailDisclosure}
          onSave={() => {
            confirmResolveDisc.open();
            refetch();
          }}
        />
      )}
      {contactDialModalDisclosure.opened && (
        <ContactDialModal
          disclosureHost={contactDialModalDisclosure}
          onSave={() => {
            confirmResolveDisc.open();
            refetch();
          }}
        />
      )}
      {smsModalDisclosure.opened && (
        <SMSModal
          disclosureHost={smsModalDisclosure}
          patientName={smsModalDisclosure.data?.name}
          patientUuid={smsModalDisclosure.data?.uuid}
          onSave={() => {
            confirmResolveDisc.open();
            refetch();
          }}
        />
      )}

      {viewType === "list" && (
        <CustomDataGridV2
          sortModel={gridSorting}
          onSortModelChange={setGridSorting}
          loading={isFetching}
          key={activeTask}
          rows={tasks?.data?.content || []}
          rowCount={tasks?.data?.page.totalElements}
          getRowId={(row: any) => row.uuid}
          columns={columns}
          checkboxSelection={isBulkSelectionEnabled}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          keepNonExistentRowsSelected
          onRowSelectionModelChange={setSelectedTaskModel}
          rowSelectionModel={selectedTaskModel}
        />
      )}
      {viewType === "calender" && (
        <TaskCalendar
          tasks={tasks?.data?.content || []}
          filterDate={filterDate}
          setFilterDate={setFilterDate}
        />
      )}
    </PageLayout>
  );
};

export default Tasks;

const getTaskDataEditPayload = (row: any): any => {
  const getObj = (obj: any) =>
    obj
      ? {
          value: Object.keys(obj ?? {})[0],
          label: Object.values(obj ?? {})[0],
        }
      : undefined;

  return {
    ...row,
    assignTo: getObj(row?.assignTo),
    actions: getObj(row?.actions),
    patient: getObj(row?.patient),
    title: row?.title ? { value: row?.title, label: row?.title } : undefined,
    recordedDate: row?.isScheduled ? new Date(row?.scheduleTime) : undefined,
    recordedTime: row?.isScheduled ? new Date(row?.scheduleTime) : undefined,
  };
};
